<template>
  <div>
    <div v-if="mini">
      <span v-if="code && !loading" class="code mini mr-5">{{ code }}</span>
      <span v-if="!code && !loading" class="code mini  mr-5">------</span>
      <span v-if="loading" class="code mini mr-5">{{ randomNumber }}</span>
    </div>
    <v-card v-if="!mini">
      <v-card-title>
        {{ title }}
      </v-card-title>
      <v-card-text>
        <slot />
        <span v-if="code && !loading" class="code">{{ code }}<div class="countdown">{{ countdownText }}</div></span>
        <span v-if="!code && !loading" class="code">------<div class="countdown" /></span>
        <span v-if="loading" class="code">{{ randomNumber }}<div class="countdown">Generating unique code</div></span>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn v-if="!noButton" color="green" @click="generateShortcode">
<v-icon class="mr-2">
mdi-dialpad
</v-icon>Generate code
</v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>

  import api from '@/helpers/api.js'
  import ShortcodeTypes from '@/cfg/shortCode-types'

  export default {
    name: 'ShortcodeGenerator',

    props: {
      action: {
        type: String,
        default: 'userLogin',
      },
      title: {
        type: String,
        default: 'Shortcode Generator',
      },
      account: {
        type: String,
        default: undefined,
      },
      user: {
        type: String,
        default: undefined,
      },
      classLearner: {
        type: String,
        default: undefined,
      },
      mini: {
        type: Boolean,
        default: false,
      },
      inviteCode: {
        type: String,
        default: undefined,
      },
      autoGenerate: {
        type: Boolean,
        default: false,
      },
      noButton: {
        type: Boolean,
        default: false,
      },
      classId: {
        type: String,
        default: undefined,
      },
      module: {
        type: String,
        default: undefined,
      },
    },

    data () {
      return {
        interval: null,
        animateInterval: null,
        randomNumber: 100000,
        countdownText: '',
        expiry: 0,
        text: '',
        expiryText: 'Code has expired',
        code: undefined,
        loading: false,
      }
    },

    destroyed () {
      clearInterval(this.interval)
    },

    async created () {
      this.animateInterval = setInterval(() => {
        this.randomNumber = Math.floor(100000 + Math.random() * 899999)
      }, 100)
    },

    mounted () {
    },

    methods: {
      sayCode () {
        var msg = new SpeechSynthesisUtterance()
        msg.text = this.code.toString().split('').join(', ')
        speechSynthesis.speak(msg)
      },

      updateCountdown (timeRemaining) {
        if (timeRemaining < 60) {
          this.countdownText = this.$t('shortCodeGenerator.expires-in-seconds', { seconds: timeRemaining })
        } else if (timeRemaining < 3600) {
          this.countdownText = this.$t('shortCodeGenerator.expires-in-minutes', { minutes: Math.floor(timeRemaining / 60) })
        } else {
          this.countdownText = this.$t('shortCodeGenerator.expires-in-hours', { hours: Math.floor(timeRemaining / 3600), minutes: Math.floor(timeRemaining / 60) % 60 })
        }
      },

      async generateShortcode () {
        this.loading = true
        clearInterval(this.interval)
        this.countdownText = 'Generating code'
        let payload = {}
        switch (this.action)
        {
          case ShortcodeTypes.UserLogin:
            payload = await api.getUserLoginShortCode()
            break
          case ShortcodeTypes.RegisterDevice:
            payload = await api.inviteDeviceShortCode()
            break
          case ShortcodeTypes.JoinClass:
            payload = await api.getJoinClassShortCode(this.inviteCode)
            break
          case ShortcodeTypes.JoinClassNoInvite:
            payload = await api.getJoinClassShortCodeNoInvite(this.classLearner)
            break
          case ShortcodeTypes.ThirdPartyUser:
            payload = await api.generateShortCodeForThirdPartyUser({ user: this.user, module: this.module })
            break
          case ShortcodeTypes.Trial:
          {
            const settings = {
              account: this.account,
              lifetime: (30 * 24 * 60 * 60),
            }
            payload = await api.getTrialShortCode(settings)
            break
          }
          case ShortcodeTypes.ClassShortCode:
          {
            payload = await api.getClassShortCode(this.classId, false)
            break
          }
          default:
            console.error('unrecognised action ' + this.action)
        }

        this.code = payload.shortCode
        if (this.code === undefined) {
          this.code = payload.id
        }
        this.expiry = payload.expires._seconds
        this.loading = false

        const dateNow = new Date()
        const timeRemaining = Math.floor(this.expiry - Math.floor(dateNow / 1000))
        this.updateCountdown(timeRemaining)
        this.interval = setInterval(() => {
          if (!this.expiry) {
            this.countdownText = ''
          }
          else {
            const dateNow = new Date()
            const timeRemaining = Math.floor(this.expiry - Math.floor(dateNow / 1000))
            this.updateCountdown(timeRemaining)
            if (timeRemaining <= 0) {
              clearInterval(this.interval)
              this.code = undefined
            }
          }
        }, 1000)
      },

      async retrieveShortcode () {
        this.loading = true
        clearInterval(this.interval)
        this.countdownText = 'Retrieving code'
        let payload = {}
        switch (this.action) {
          case ShortcodeTypes.UserLogin:
            // this is not implemented
            break
          case ShortcodeTypes.RegisterDevice:
            // this is not implemented
            break
          case ShortcodeTypes.JoinClass:
            // this is not implemented
            break
          case ShortcodeTypes.JoinClassNoInvite:
            // this is not implemented
            break
          case ShortcodeTypes.ThirdPartyUser:
            // this is not implemented
            break
          case ShortcodeTypes.Trial:
            // this is not implemented
            break
          case ShortcodeTypes.ClassShortCode:
          {
            payload = await api.getClassShortCode(this.classId, true)
            break
          }
          default:
            console.error('unrecognised action ' + this.action)
        }

        this.code = payload.shortCode
        if (this.code === undefined) {
          this.code = payload.id
        }
        this.expiry = payload.expires._seconds
        this.loading = false

        const dateNow = new Date()
        const timeRemaining = Math.floor(this.expiry - Math.floor(dateNow / 1000))
        this.updateCountdown(timeRemaining)
        this.interval = setInterval(() => {
          if (!this.expiry) {
            this.countdownText = ''
          }
          else {
            const dateNow = new Date()
            const timeRemaining = Math.floor(this.expiry - Math.floor(dateNow / 1000))
            this.updateCountdown(timeRemaining)
            if (timeRemaining <= 0) {
              clearInterval(this.interval)
              this.code = undefined
            }
          }
        }, 1000)
      },
    },
  }
</script>

<style scoped>
#mark {
  width: 32px;
  margin-right: 10px;
}

.code {
  font-size: 36px;
  font-weight: 600;
  letter-spacing: 4px;
  text-align: center;
  padding: 20px;
  display: block;
  background: #295478;
  margin: 10px 0;
  color: white;
  line-height: 36px;
}

.mini {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 4px;
  text-align: center;
  padding: 10px;
  display: inline-block;
  width: 200px;
  background: #295478;
  color: white;
  line-height: normal;
  margin: 0px;
}



</style>
